import cuid from 'cuid';
import { isEmpty, isPlainObject } from 'lodash';

import { TRACKING_TTL_MS } from './constants';

const TRACKING_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'rx_source',
  'rx_medium',
  'rx_job',
];

const storageKey = (companyId: string, boardSlug: string) =>
  `jobBoardTracking:${companyId}/${boardSlug}`;

const visitorKey = (companyId: string) => `jobBoardVisitor:${companyId}`;

const withStorage = <T>(access: () => T): T | null => {
  try {
    return access();
  } catch {
    return null;
  }
};

export const parseTrackingParams = (search: string): JobBoardTracking => {
  const params = new URLSearchParams(search);

  return Object.fromEntries(
    TRACKING_PARAMS.map((key): [string, string] => [
      key,
      (params.get(key) ?? '').trim(),
    ]).filter(([, value]) => value)
  );
};

const readStored = (
  companyId: string,
  boardSlug: string
): JobBoardTracking | null => {
  const key = storageKey(companyId, boardSlug);
  const parsed: unknown = withStorage(() =>
    JSON.parse(localStorage.getItem(key) ?? 'null')
  );

  if (!isPlainObject(parsed)) return null;

  const { tracking, storedAt } = parsed as JobBoardStoredTracking;

  if (Date.now() - storedAt < TRACKING_TTL_MS) return tracking;

  withStorage(() => localStorage.removeItem(key));
  return null;
};

export const rememberTracking = (
  companyId: string,
  boardSlug: string,
  search: string
) => {
  const tracking = parseTrackingParams(search);

  if (isEmpty(tracking) || readStored(companyId, boardSlug)) return;

  withStorage(() =>
    localStorage.setItem(
      storageKey(companyId, boardSlug),
      JSON.stringify({ tracking, storedAt: Date.now() })
    )
  );
};

const visitorId = (companyId: string): string => {
  const key = visitorKey(companyId);
  const stored = withStorage(() => localStorage.getItem(key));

  if (stored) return stored;

  const id = cuid();
  withStorage(() => localStorage.setItem(key, id));
  return id;
};

export const storedTracking = (
  companyId: string,
  boardSlug: string
): JobBoardTracking => ({
  ...readStored(companyId, boardSlug),
  visitor_id: visitorId(companyId),
});
